import { BarChart3, CircleAlert, ShieldCheck, TrendingUp } from "lucide-react";
import { useApi } from "@/hooks/use-api";
import { SkeletonCard } from "@/components/skeleton";
import { formatNumber } from "@/lib/i18n";

interface BenchmarkMetrics {
  totalSent: number;
  totalEngaged: number;
  engagementRate: number;
}

interface IndustryBenchmarkSnapshot {
  industry: string | null;
  cohortSize: number;
  benchmarkAvailable: boolean;
  reason: "industry_missing" | "insufficient_data" | null;
  tenantMetrics: BenchmarkMetrics | null;
  industryMetrics: BenchmarkMetrics | null;
  periodDays: number;
}

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function renderEmptyReason(reason: IndustryBenchmarkSnapshot["reason"]) {
  switch (reason) {
    case "industry_missing":
      return {
        title: "Set your industry to unlock benchmarks",
        description: "This tenant does not yet expose a canonical industry. Update the business profile first so the benchmark cohort can resolve safely.",
      };
    case "insufficient_data":
      return {
        title: "Benchmark cohort is still below the privacy floor",
        description: "Cross-tenant comparison stays hidden until at least 25 active tenants exist in the same industry. This page remains truthful instead of leaking thin cohort data.",
      };
    default:
      return {
        title: "Benchmarks are not available yet",
        description: "The route is mounted, but no benchmark comparison can be shown yet.",
      };
  }
}

function ComparisonRow({
  label,
  tenantValue,
  industryValue,
  format,
}: {
  label: string;
  tenantValue: number;
  industryValue: number;
  format: (value: number) => string;
}) {
  const max = Math.max(tenantValue, industryValue, 1);
  const tenantWidth = Math.max((tenantValue / max) * 100, tenantValue > 0 ? 6 : 0);
  const industryWidth = Math.max((industryValue / max) * 100, industryValue > 0 ? 6 : 0);
  const isAhead = tenantValue >= industryValue;

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-[var(--color-foreground)]">{label}</div>
          <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {isAhead ? "Ahead of industry average" : "Below industry average"}
          </div>
        </div>
        <div className={`text-xs font-medium ${isAhead ? "text-[var(--color-success)]" : "text-[var(--color-warning)]"}`}>
          {isAhead ? "Above avg" : "Needs lift"}
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <div className="mb-1 flex items-center justify-between text-xs text-[var(--color-muted-foreground)]">
            <span>Your tenant</span>
            <span className="font-medium text-[var(--color-foreground)]">{format(tenantValue)}</span>
          </div>
          <div className="h-2 rounded-full bg-[var(--color-muted)]">
            <div
              className="h-2 rounded-full bg-[var(--color-primary)] transition-[width]"
              style={{ width: `${tenantWidth}%` }}
            />
          </div>
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between text-xs text-[var(--color-muted-foreground)]">
            <span>Industry average</span>
            <span className="font-medium text-[var(--color-foreground)]">{format(industryValue)}</span>
          </div>
          <div className="h-2 rounded-full bg-[var(--color-muted)]">
            <div
              className="h-2 rounded-full bg-[var(--color-accent)] transition-[width]"
              style={{ width: `${industryWidth}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BenchmarkExplorerPage() {
  const { data, isLoading, error } = useApi<IndustryBenchmarkSnapshot>("/v1/analytics/benchmarks");
  const emptyReason = renderEmptyReason(data?.reason ?? null);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Industry Benchmarks</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Tenant-authenticated benchmark comparisons from the canonical analytics route. Privacy floor stays enforced at k≥25.
          </p>
        </div>
        {data?.benchmarkAvailable ? (
          <div className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-1.5 text-xs text-[var(--color-muted-foreground)]">
            Rolling {data.periodDays}-day comparison
          </div>
        ) : null}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <SkeletonCard key={index} className="h-40" />
          ))}
        </div>
      ) : error ? (
        <div className="rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 p-6 text-sm text-[var(--color-error)]">
          Failed to load industry benchmarks. {error}
        </div>
      ) : !data?.benchmarkAvailable || !data.industryMetrics || !data.tenantMetrics ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-background)] p-12 text-center">
          <BarChart3 className="mx-auto mb-3 h-10 w-10 text-[var(--color-muted-foreground)]" />
          <p className="font-medium text-[var(--color-foreground)]">{emptyReason.title}</p>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{emptyReason.description}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-[var(--color-foreground)]">{data.industry}</h2>
                  <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                    Cohort size: {data.cohortSize} active tenants over the last {data.periodDays} days.
                  </p>
                </div>
                <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-success)]/10 px-2.5 py-1 text-xs font-medium text-[var(--color-success)]">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  k≥25 enforced
                </span>
              </div>

              <div className="space-y-4">
                <ComparisonRow
                  label="Sent volume"
                  tenantValue={data.tenantMetrics.totalSent}
                  industryValue={data.industryMetrics.totalSent}
                  format={formatNumber}
                />
                <ComparisonRow
                  label="Engaged events"
                  tenantValue={data.tenantMetrics.totalEngaged}
                  industryValue={data.industryMetrics.totalEngaged}
                  format={formatNumber}
                />
                <ComparisonRow
                  label="Engagement rate"
                  tenantValue={data.tenantMetrics.engagementRate}
                  industryValue={data.industryMetrics.engagementRate}
                  format={formatPercent}
                />
              </div>
            </div>

            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-[var(--color-foreground)]">
                <TrendingUp className="h-4 w-4 text-[var(--color-primary)]" />
                Comparison Summary
              </div>

              <div className="mt-4 space-y-3">
                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4">
                  <div className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">Your tenant</div>
                  <div className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">{formatPercent(data.tenantMetrics.engagementRate)}</div>
                  <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">Engagement rate across sent communication events.</div>
                </div>

                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4">
                  <div className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">Industry average</div>
                  <div className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">{formatPercent(data.industryMetrics.engagementRate)}</div>
                  <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">Average per-tenant cohort engagement over the same 90-day window.</div>
                </div>

                <div className="rounded-lg border border-dashed border-[var(--color-border)] p-4 text-sm text-[var(--color-muted-foreground)]">
                  <div className="flex items-center gap-2 text-[var(--color-foreground)]">
                    <CircleAlert className="h-4 w-4" />
                    Comparison logic
                  </div>
                  <div className="mt-2">
                    Industry aggregates come from ClickHouse `benchmark_by_industry_mv` with the 25-tenant privacy guard applied at query time. Your metrics come from tenant-scoped `events_daily_mv`.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
